/**
 * <selfie-studio client="..." campaign="...">
 *
 * La URL de la API NO se pasa por atributo: queda horneada en widget.js en
 * build time desde VITE_API_URL (.env). Vacía → rutas relativas (/api/...),
 * útil en dev con el proxy de Vite o si el widget se sirve del mismo dominio.
 *
 * Atributos de personalización:
 *   dark             — interfaz oscura
 *   translucent      — sin fondo/borde (se integra al sitio anfitrión)
 *   hide-branding    — oculta el footer "Powered by SelfieStudio"
 *   primary-color    — color de acento (botones, spinner), ej. "#e11d48"
 *   radius           — border-radius del widget, ej. "0" o "24px"
 *   max-width        — ancho máximo, ej. "560px" o "100%"
 *   gallery          — modo de la galería de plantillas: "grid" (default) | "carousel"
 *   gallery-columns  — columnas del grid (default 2)
 *   tips             — guía rápida del loader, textos separados por "|"
 *
 * Wrapper Lit: aporta el Shadow DOM (aislamiento de CSS/JS del sitio anfitrión)
 * y monta dentro la app Svelte 5 que implementa las vistas del widget.
 * dark/translucent/hide-branding se resuelven 100% en CSS vía :host([attr]);
 * los atributos con valor se traducen a custom properties en updated().
 */
import { LitElement, type PropertyValues } from 'lit';
import { mount, unmount } from 'svelte';
import App from './components/App.svelte';
import { widgetStyles } from './styles';

// Resuelto en build time por Vite (VITE_API_URL del .env)
const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export class SelfieStudioElement extends LitElement {
	static properties = {
		client: { type: String },
		campaign: { type: String },
		dark: { type: Boolean, reflect: true },
		translucent: { type: Boolean, reflect: true },
		hideBranding: { type: Boolean, attribute: 'hide-branding', reflect: true },
		primaryColor: { type: String, attribute: 'primary-color' },
		radius: { type: String },
		maxWidth: { type: String, attribute: 'max-width' },
		gallery: { type: String },
		galleryColumns: { type: Number, attribute: 'gallery-columns' },
		tips: { type: String }
	};

	declare client: string;
	declare campaign: string;
	declare dark: boolean;
	declare translucent: boolean;
	declare hideBranding: boolean;
	declare primaryColor: string;
	declare radius: string;
	declare maxWidth: string;
	declare gallery: 'grid' | 'carousel';
	declare galleryColumns: number;
	declare tips: string;

	#app: Record<string, unknown> | null = null;

	constructor() {
		super();
		this.client = '';
		this.campaign = '';
		this.dark = false;
		this.translucent = false;
		this.hideBranding = false;
		this.primaryColor = '';
		this.radius = '';
		this.maxWidth = '';
		this.gallery = 'grid';
		this.galleryColumns = 2;
		this.tips = '';
	}

	protected updated(_changed: PropertyValues) {
		// Atributos con valor → custom properties que consume styles.ts
		this.#applyCssVar('--ss-primary', this.primaryColor);
		this.#applyCssVar('--ss-radius', this.radius);
		this.#applyCssVar('--ss-max-width', this.maxWidth);
		this.#applyCssVar('--ss-gallery-cols', this.galleryColumns > 0 ? String(this.galleryColumns) : '');
	}

	#applyCssVar(name: string, value: string) {
		if (value) {
			this.style.setProperty(name, value);
		} else {
			this.style.removeProperty(name);
		}
	}

	// La app Svelte gestiona su propio DOM dentro del shadow root
	createRenderRoot() {
		return this.attachShadow({ mode: 'open' });
	}

	connectedCallback() {
		super.connectedCallback();

		const style = document.createElement('style');
		style.textContent = widgetStyles;
		this.renderRoot.appendChild(style);

		const container = document.createElement('div');
		this.renderRoot.appendChild(container);

		this.#app = mount(App, {
			target: container,
			props: {
				apiUrl: API_URL,
				clientToken: this.client,
				campaignToken: this.campaign,
				galleryMode: this.gallery === 'carousel' ? 'carousel' : 'grid',
				customTips: this.tips
					.split('|')
					.map((tip) => tip.trim())
					.filter(Boolean)
			}
		});
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.#app) {
			unmount(this.#app);
			this.#app = null;
		}
	}
}
